// components/TakeoffPage.jsx
// Takeoff launcher.  Projects | Opportunities  →  pick one  →  a grid of its saved takeoffs
// (open / rename / delete / new)  →  TakeoffViewer  →  Estimate page.
// Project takeoffs can be published to the budget; Opportunity (bid-stage) takeoffs share the
// exact same estimate math and pricing view, but can't be published until the job is awarded.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import TakeoffViewer from "./TakeoffViewer";
import { MovableHost } from "./FullscreenHost";
import TakeoffEstimatePage from "./TakeoffEstimatePage";
import SpecScanner from "./SpecScanner";
import RfiManager from "./RfiManager";
import { ImageMarkup, uploadRfiImage } from "./RfiImageTools";
import { DEFAULT_SETTINGS } from "../lib/takeoff/estimateCalc";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "X-Tenant-ID": localStorage.getItem("tenantId"),
});

const cls = (...c) => c.filter(Boolean).join(" ");

const timeAgo = (d) => {
  if (!d) return "";
  const s = (Date.now() - new Date(d).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 14) return `${Math.floor(s / 86400)}d ago`;
  return new Date(d).toLocaleDateString();
};

const STATUS_STYLE = {
  Undecided: "bg-amber-500/15 text-amber-300",
  Won: "bg-emerald-500/15 text-emerald-300",
  Lost: "bg-red-500/15 text-red-300",
  "No Opportunity": "bg-gray-500/20 text-gray-400",
};

// ── one saved-takeoff card ──────────────────────────────────────────────────
function TakeoffCard({ t, opening, onOpen, onRename, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(t.fileName);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (editing) setTimeout(() => ref.current?.select(), 20);
  }, [editing]);

  const commit = async () => {
    const name = val.trim();
    if (!name || name === t.fileName) {
      setEditing(false);
      setErr("");
      setVal(t.fileName);
      return;
    }
    setSaving(true);
    setErr("");
    try {
      await onRename(t, name);
      setEditing(false);
    } catch (e) {
      setErr(e?.response?.data?.error || "Rename failed");
    } finally {
      setSaving(false);
    }
  };

  const conds = t.conditions || [];
  return (
    <div className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-gray-900 transition hover:border-orange-500/50 hover:bg-gray-900">
      {/* colour strip of the conditions on this takeoff */}
      <div className="flex h-1.5">
        {conds.length ? (
          conds
            .slice(0, 12)
            .map((c) => (
              <div
                key={c.id}
                className="flex-1"
                style={{ background: c.color }}
              />
            ))
        ) : (
          <div className="flex-1 bg-gray-800" />
        )}
      </div>
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-start gap-2">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-orange-500/10 text-[10px] font-bold text-orange-300">
            PDF
          </div>
          <div className="min-w-0 flex-1">
            {editing ? (
              <input
                ref={ref}
                value={val}
                disabled={saving}
                onChange={(e) => setVal(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commit();
                  if (e.key === "Escape") {
                    setEditing(false);
                    setVal(t.fileName);
                    setErr("");
                  }
                }}
                className="w-full rounded-md border border-orange-500 bg-gray-950 px-2 py-1 text-sm text-white focus:outline-none"
              />
            ) : (
              <button
                onClick={() => onOpen(t)}
                className="block w-full truncate text-left text-sm font-semibold text-white hover:text-orange-300"
                title={t.fileName}
              >
                {t.fileName}
              </button>
            )}
            {err && <div className="mt-1 text-[11px] text-red-300">{err}</div>}
            <div className="mt-0.5 flex items-center gap-2 text-[11px] text-gray-500">
              <span
                className={cls(
                  "rounded-full px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide",
                  t.status === "published"
                    ? "bg-emerald-500/15 text-emerald-300"
                    : "bg-amber-500/15 text-amber-300",
                )}
              >
                {t.status || "draft"}
              </span>
              <span>{timeAgo(t.updatedAt)}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 text-center">
          {[
            [conds.length, "assemblies"],
            [t.pageCount || 0, "sheets"],
            [t.gcsPath ? "✓" : "—", "drawing"],
          ].map(([v, l]) => (
            <div key={l} className="rounded-lg bg-black/25 py-1.5">
              <div className="text-sm font-semibold text-gray-100">{v}</div>
              <div className="text-[9.5px] uppercase tracking-wider text-gray-500">
                {l}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-auto flex items-center gap-1.5">
          <button
            onClick={() => onOpen(t)}
            disabled={opening}
            className="flex-1 rounded-lg bg-orange-500 py-1.5 text-xs font-semibold text-white hover:bg-orange-400 disabled:opacity-60"
          >
            {opening ? "Opening…" : "Open"}
          </button>
          <button
            onClick={() => setEditing(true)}
            className="rounded-lg bg-white/5 px-2.5 py-1.5 text-xs text-gray-300 hover:bg-white/10"
          >
            Rename
          </button>
          <button
            onClick={() => onDelete(t)}
            className="rounded-lg bg-white/5 px-2.5 py-1.5 text-xs text-gray-400 hover:bg-red-500/20 hover:text-red-300"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TakeoffPage() {
  const [kind, setKind] = useState("project"); // "project" | "opportunity"
  const [projects, setProjects] = useState([]);
  const [opps, setOpps] = useState([]);
  const [counts, setCounts] = useState({ project: {}, opportunity: {} });
  const [search, setSearch] = useState("");
  const [oppStatus, setOppStatus] = useState("open"); // open | all
  const [owner, setOwner] = useState(null); // project or opportunity doc
  const [listLoading, setListLoading] = useState(true);

  const [takeoffs, setTakeoffs] = useState([]);
  const [takeoffsLoading, setTakeoffsLoading] = useState(false);
  const [openingId, setOpeningId] = useState(null);

  // viewer / estimate
  const [active, setActive] = useState(null); // { initialData, fileUrl, key }
  const [view, setView] = useState("viewer"); // "viewer" | "estimate"
  const [estimatePayload, setEstimatePayload] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [condRenames, setCondRenames] = useState({}); // { [condId]: newName } — pushed into the viewer
  const [error, setError] = useState("");

  const ownerId = owner?._id;
  const ownerLabel = owner
    ? kind === "opportunity"
      ? `${owner.title || "Opportunity"}${owner.account?.companyName ? ` · ${owner.account.companyName}` : ""}`
      : `${owner.projectName || owner.name || "Project"}${owner.projectNumber ? ` · #${owner.projectNumber}` : ""}`
    : "";
  const ownerKey = kind === "opportunity" ? "opportunityId" : "projectId";

  // ── specs & RFIs ──────────────────────────────────────────────────────────
  const [panel, setPanel] = useState(null); // launcher: null | "specs" | "rfis"
  const [rfis, setRfis] = useState([]);
  const [rfiInbox, setRfiInbox] = useState([]); // questions sent over from the spec scanner
  const [pinMode, setPinMode] = useState(null); // null | "pick" | { rfiId, itemId }
  const [pinAsk, setPinAsk] = useState(null); // { page, lat, lng } — a pin placed with the RFI tool
  const [pinForm, setPinForm] = useState({ rfiId: "new", question: "" });
  const [focusPin, setFocusPin] = useState(null);
  const [focusRfiId, setFocusRfiId] = useState(null);
  const [toast, setToast] = useState("");
  const [curTakeoffId, setCurTakeoffId] = useState(null);
  // full screen: the drawing's own full screen carries over to the estimate / specs / RFIs; the estimate can also
  // go full screen on its own
  const [viewerFull, setViewerFull] = useState(false);
  const [estFull, setEstFull] = useState(false);
  const viewerDataRef = useRef(null); // the viewer fills it: () => its current takeoff data
  const ownerQ = owner ? { [ownerKey]: owner._id } : null;
  const loadRfis = useCallback(async () => {
    if (!owner?._id) return setRfis([]);
    try {
      const r = await axios.get(`${API}/rfis`, {
        headers: authHeaders(),
        params: { [ownerKey]: owner._id },
      });
      setRfis(r.data?.rfis || []);
    } catch {
      setRfis([]); // (RFI routes not mounted yet)
    }
  }, [owner?._id, ownerKey]); // eslint-disable-line
  useEffect(() => {
    loadRfis();
    setRfiInbox([]);
  }, [loadRfis]);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 6000);
    return () => clearTimeout(t);
  }, [toast]);
  const rfiPins = useMemo(
    () =>
      rfis
        .filter((r) => r.status !== "void")
        .flatMap((r) =>
          (r.pins || [])
            .filter(
              (p) =>
                !p.takeoffId ||
                !curTakeoffId ||
                String(p.takeoffId) === String(curTakeoffId),
            )
            .map((p) => {
              const k = (r.items || []).findIndex((i) => i.id === p.itemId);
              return {
                ...p,
                rfiId: r._id,
                number: r.number,
                status: r.status,
                q: k >= 0 ? k + 1 : null,
                title: k >= 0 ? r.items[k].question : r.title,
              };
            }),
        ),
    [rfis, curTakeoffId],
  );
  const rfiMeta = {
    company: localStorage.getItem("companyName") || "",
    preparedBy: localStorage.getItem("userDisplayName") || "",
  };
  const putRfi = (rfi) =>
    setRfis((l) =>
      l.some((x) => x._id === rfi._id)
        ? l.map((x) => (x._id === rfi._id ? rfi : x))
        : [rfi, ...l],
    );
  const newPinId = () => Math.random().toString(36).slice(2, 10);
  // a click on the drawing with the RFI pin tool
  const placeRfiPin = async (page, [lat, lng]) => {
    if (pinMode && typeof pinMode === "object") {
      const r = rfis.find((x) => x._id === pinMode.rfiId);
      setPinMode(null);
      if (!r) return;
      try {
        const pin = {
          id: newPinId(),
          takeoffId: curTakeoffId,
          page,
          lat,
          lng,
          itemId: pinMode.itemId,
        };
        const saved = (
          await axios.put(
            `${API}/rfis/${r._id}`,
            { pins: [...(r.pins || []), pin] },
            { headers: authHeaders() },
          )
        ).data.rfi;
        putRfi(saved);
        const k = (saved.items || []).findIndex((i) => i.id === pinMode.itemId);
        setToast(
          `📍 Pinned RFI ${saved.number}${k >= 0 ? ` question ${k + 1}` : ""}`,
        );
      } catch {
        setToast("Couldn't save the pin");
      }
      return;
    }
    setPinForm((f) => ({
      rfiId: rfis.find((x) => x.status === "draft")?._id || "new",
      question: "",
    }));
    setPinAsk({ page, lat, lng });
  };
  const savePinAsk = async () => {
    const { page, lat, lng } = pinAsk;
    const item = {
      id: newPinId(),
      question: pinForm.question.trim(),
      assumption: "",
      source: { kind: "drawing", label: `Page ${page} · pinned`, page },
      response: "",
      answered: false,
    };
    const pin = {
      id: newPinId(),
      takeoffId: curTakeoffId,
      page,
      lat,
      lng,
      itemId: item.id,
    };
    try {
      let saved;
      if (pinForm.rfiId === "new")
        saved = (
          await axios.post(
            `${API}/rfis`,
            {
              ...ownerQ,
              title: "Drywall clarifications",
              items: [item],
              pins: [pin],
            },
            { headers: authHeaders() },
          )
        ).data.rfi;
      else {
        const r = rfis.find((x) => x._id === pinForm.rfiId);
        saved = (
          await axios.put(
            `${API}/rfis/${r._id}`,
            {
              items: [...(r.items || []), item],
              pins: [...(r.pins || []), pin],
            },
            { headers: authHeaders() },
          )
        ).data.rfi;
      }
      putRfi(saved);
      setToast(`📍 Added to RFI ${saved.number}`);
      setPinAsk(null);
      setPinMode(null);
    } catch (e) {
      setToast(e?.response?.data?.message || "Couldn't save the RFI");
    }
  };
  // ── pictures cut from the drawing for an RFI ──
  const [snipMode, setSnipMode] = useState(null); // null | "pick" | { rfiId, itemId }
  const [snipShot, setSnipShot] = useState(null); // { src, page, target }
  const [snipForm, setSnipForm] = useState({
    rfiId: "new",
    itemId: "new",
    question: "",
  });
  const [snipSaving, setSnipSaving] = useState(false);
  const startSnip = (target) => {
    setPinMode(null);
    setSnipMode(target);
    setView("viewer");
  };
  const gotSnip = (src, { page }) => {
    const target = snipMode;
    setSnipMode(null);
    const draftRfi = rfis.find((r) => r.status === "draft");
    setSnipForm({ rfiId: draftRfi?._id || "new", itemId: "new", question: "" });
    setSnipShot({ src, page, target });
  };
  const saveSnip = async ({ blob, w, h, caption }) => {
    const { page, target } = snipShot;
    setSnipSaving(true);
    try {
      let rfiId, itemId;
      if (target && typeof target === "object") ({ rfiId, itemId } = target);
      else if (snipForm.rfiId === "new") {
        itemId = newPinId();
        const r = (
          await axios.post(
            `${API}/rfis`,
            {
              ...ownerQ,
              title: "Drywall clarifications",
              items: [
                {
                  id: itemId,
                  question: snipForm.question.trim(),
                  source: { kind: "drawing", label: `Page ${page}`, page },
                },
              ],
            },
            { headers: authHeaders() },
          )
        ).data.rfi;
        rfiId = r._id;
      } else {
        rfiId = snipForm.rfiId;
        itemId = snipForm.itemId;
        if (itemId === "new") {
          const r = rfis.find((x) => x._id === rfiId);
          itemId = newPinId();
          await axios.put(
            `${API}/rfis/${rfiId}`,
            {
              items: [
                ...(r.items || []),
                {
                  id: itemId,
                  question: snipForm.question.trim(),
                  source: { kind: "drawing", label: `Page ${page}`, page },
                },
              ],
            },
            { headers: authHeaders() },
          );
        }
      }
      const saved = await uploadRfiImage(rfiId, itemId, blob, {
        w,
        h,
        caption,
        source: "drawing",
        page,
        takeoffId: curTakeoffId || "",
      });
      putRfi(saved);
      const k = saved.items.findIndex((i) => i.id === itemId);
      setToast(
        `🖼 Picture added to RFI ${saved.number}${k >= 0 ? ` question ${k + 1}` : ""}`,
      );
      setSnipShot(null);
      if (target && typeof target === "object") setView("rfis"); // came from the RFI — go back to it
    } catch (e) {
      setToast(e?.response?.data?.message || "Couldn't save the picture");
    } finally {
      setSnipSaving(false);
    }
  };

  const addToRfiInbox = (item) =>
    setRfiInbox((l) =>
      l.some((x) => x.key === item.source?.key)
        ? l
        : [
            ...l,
            {
              key: item.source?.key || newPinId(),
              question: item.question,
              why: item.why,
              severity: "med",
              source: item.source,
            },
          ],
    );

  // ── owners ────────────────────────────────────────────────────────────────
  useEffect(() => {
    setListLoading(true);
    Promise.all([
      axios
        .get(`${API}/projects`, { headers: authHeaders() })
        .then((r) => {
          const l = r.data?.projects || r.data || [];
          setProjects(Array.isArray(l) ? l : []);
        })
        .catch(() => setError("Could not load projects.")),
      axios
        .get(`${API}/opportunities`, {
          headers: authHeaders(),
          params: { limit: 1000, sortField: "updatedAt", sortOrder: "desc" },
        })
        .then((r) => setOpps(r.data?.opportunities || []))
        .catch(() => {}),
    ]).finally(() => setListLoading(false));
  }, []);

  const loadCounts = useCallback(() => {
    ["project", "opportunity"].forEach((k) =>
      axios
        .get(`${API}/takeoffs3/overview`, {
          headers: authHeaders(),
          params: { kind: k },
        })
        .then((r) => {
          const m = {};
          (r.data || []).forEach((t) => {
            const id = k === "project" ? t.projectId : t.opportunityId;
            if (id) m[id] = (m[id] || 0) + 1;
          });
          setCounts((c) => ({ ...c, [k]: m }));
        })
        .catch(() => {}),
    );
  }, []);
  useEffect(loadCounts, [loadCounts]);

  const owners = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (kind === "project")
      return projects
        .filter((p) =>
          !q
            ? true
            : `${p.projectName || p.name || ""} ${p.projectNumber || ""}`
                .toLowerCase()
                .includes(q),
        )
        .sort(
          (a, b) =>
            (counts.project[b._id] || 0) - (counts.project[a._id] || 0) ||
            String(a.projectName || a.name).localeCompare(
              String(b.projectName || b.name),
            ),
        );
    return opps
      .filter((o) => (oppStatus === "open" ? o.status === "Undecided" : true))
      .filter((o) =>
        !q
          ? true
          : `${o.title || ""} ${o.account?.companyName || ""}`
              .toLowerCase()
              .includes(q),
      );
  }, [kind, projects, opps, search, oppStatus, counts]);

  // ── takeoffs of the chosen owner ──────────────────────────────────────────
  const loadTakeoffs = useCallback(
    (id, k = kind) => {
      setTakeoffsLoading(true);
      axios
        .get(
          `${API}/takeoffs3/${k === "opportunity" ? "opportunity" : "project"}/${id}`,
          { headers: authHeaders() },
        )
        .then((r) => setTakeoffs(r.data || []))
        .catch(() => setError("Could not load takeoffs."))
        .finally(() => setTakeoffsLoading(false));
    },
    [kind],
  );

  const pickOwner = (o) => {
    setPanel(null);
    setOwner(o);
    setActive(null);
    setError("");
    loadTakeoffs(o._id);
  };

  const switchKind = (k) => {
    setKind(k);
    setOwner(null);
    setTakeoffs([]);
    setSearch("");
    setActive(null);
  };

  // ── open ──────────────────────────────────────────────────────────────────
  const getDrawingUrl = async (gcsPath) => {
    if (!gcsPath) return null;
    try {
      const r = await axios.get(`${API}/takeoffs3/drawing-url`, {
        headers: authHeaders(),
        params: { path: gcsPath },
      });
      return r.data?.url || null;
    } catch {
      return null;
    }
  };

  const openById = async (id) => {
    const r = await axios.get(`${API}/takeoffs3`, {
      headers: authHeaders(),
      params: { [ownerKey]: ownerId, id },
    });
    const data = r.data;
    const fileUrl = data?.gcsPath ? await getDrawingUrl(data.gcsPath) : null;
    const st = { ...DEFAULT_SETTINGS, ...(data?.estimateSettings || {}) };
    setMappings(data?.mappings || []);
    mapRef.current = data?.mappings || [];
    setCondRenames({});
    setSettings(st);
    setRef.current = st;
    // what the server has — estimate saves send only what changes from here
    estBase.current =
      typeof data?.rev === "number" ? baseFrom(data?.mappings || [], st) : null;
    setCurTakeoffId(data?._id ? String(data._id) : null);
    setPanel(null);
    setPinMode(null);
    revRef.current = data?.rev || 0;
    ownRevs.current = new Set();
    seenRevs.current = new Set();
    setEstimatePayload(null);
    setView("viewer");
    setActive({ initialData: data, fileUrl, key: `${data._id}-${Date.now()}` });
  };

  const openTakeoff = async (t) => {
    setOpeningId(t._id);
    setError("");
    try {
      await openById(t._id);
    } catch (e) {
      console.error(e);
      setError("Could not load that takeoff.");
    } finally {
      setOpeningId(null);
    }
  };

  const newTakeoff = () => {
    setMappings([]);
    mapRef.current = [];
    setCondRenames({});
    setSettings(DEFAULT_SETTINGS);
    setRef.current = DEFAULT_SETTINGS;
    estBase.current = null; // a new takeoff: the first save sends everything
    setCurTakeoffId(null);
    setPanel(null);
    revRef.current = 0;
    ownRevs.current = new Set();
    seenRevs.current = new Set();
    setEstimatePayload(null);
    setView("viewer");
    setActive({ initialData: null, fileUrl: null, key: `new-${Date.now()}` });
  };

  // ── rename / delete ───────────────────────────────────────────────────────
  const renameTakeoff = async (t, name) => {
    const r = await axios.patch(
      `${API}/takeoffs3/${t._id}/rename`,
      { fileName: name },
      { headers: authHeaders() },
    );
    setTakeoffs((l) =>
      l.map((x) => (x._id === t._id ? { ...x, fileName: r.data.fileName } : x)),
    );
    return r.data;
  };

  const deleteTakeoff = async (t) => {
    if (
      !window.confirm(
        `Delete "${t.fileName}"?${t.status === "published" ? "\n\nIt has been published to the budget — the budget rows stay." : ""}\nThe takeoff and its drawing are removed.`,
      )
    )
      return;
    try {
      await axios.delete(`${API}/takeoffs3/${t._id}`, {
        headers: authHeaders(),
      });
      setTakeoffs((l) => l.filter((x) => x._id !== t._id));
      loadCounts();
    } catch {
      setError("Could not delete that takeoff.");
    }
  };

  // ── viewer wiring ─────────────────────────────────────────────────────────
  // Mappings + estimate settings live on the estimate page; every save from either place carries what changed.
  const mapRef = useRef(mappings);
  const setRef = useRef(settings);
  mapRef.current = mappings;
  setRef.current = settings;

  // The estimate is saved ONE ASSEMBLY AT A TIME: estBase is what the server has (per conditionId), so a save
  // sends only the assemblies changed here (ops.mappings). Two people pricing different assemblies never
  // overwrite each other; their changes come back (save replies + the viewer's 4-second check) and are merged
  // in unless the same assembly was changed here and not saved yet (then ours is saved next and wins).
  const mjson = (x) => JSON.stringify(x ?? null);
  const estBase = useRef(null); // { m: Map(conditionId → json), s: json } — null until the takeoff exists
  const ownRevs = useRef(new Set()); // revisions saved from this screen
  const seenRevs = useRef(new Set());
  const revRef = useRef(0);
  const estClientId = useRef(`est-${Math.random().toString(36).slice(2, 10)}`);
  const queue = useRef(Promise.resolve());
  const baseFrom = (maps, st) => ({
    m: new Map((maps || []).map((x) => [x.conditionId, mjson(x)])),
    s: mjson(st),
  });
  const estimateOps = () => {
    const b = estBase.current;
    const cur = mapRef.current || [];
    const upsert = [];
    const seen = new Set();
    for (const x of cur) {
      seen.add(x.conditionId);
      if (b.m.get(x.conditionId) !== mjson(x)) upsert.push(x);
    }
    const remove = [...b.m.keys()].filter((cid) => !seen.has(cid));
    const st = mjson(setRef.current);
    return {
      mappings: upsert.length || remove.length ? { upsert, remove } : null,
      settings: st !== b.s ? setRef.current : undefined,
    };
  };

  // other people's estimate changes → here
  const applyRemoteEstimate = (entries) => {
    const b = estBase.current;
    if (!b || !entries?.length) return;
    const fresh = entries
      .filter(
        (e) =>
          e &&
          typeof e.rev === "number" &&
          !ownRevs.current.has(e.rev) &&
          !seenRevs.current.has(e.rev),
      )
      .sort((a, c) => a.rev - c.rev);
    if (!fresh.length) return;
    const upd = new Map();
    let newS;
    for (const e of fresh) {
      seenRevs.current.add(e.rev);
      revRef.current = Math.max(revRef.current, e.rev);
      const o = e.ops || {};
      for (const x of o.mappings?.upsert || [])
        upd.set(x.conditionId, { obj: x });
      for (const cid of o.mappings?.remove || [])
        upd.set(cid, { remove: true });
      if (Array.isArray(o.set?.mappings)) {
        // an older screen saved the whole list
        const ids = new Set(o.set.mappings.map((x) => x.conditionId));
        for (const x of o.set.mappings) upd.set(x.conditionId, { obj: x });
        for (const cid of b.m.keys())
          if (!ids.has(cid)) upd.set(cid, { remove: true });
      }
      if (o.set?.estimateSettings !== undefined) newS = o.set.estimateSettings;
    }
    if (upd.size) {
      const cur = mapRef.current || [];
      const mine = new Map(cur.map((x) => [x.conditionId, x]));
      let next = cur;
      let changed = false;
      for (const [cid, ch] of upd) {
        const dirty = mjson(mine.get(cid)) !== (b.m.get(cid) ?? mjson(null));
        if (ch.remove) b.m.delete(cid);
        else b.m.set(cid, mjson(ch.obj));
        if (dirty) continue; // changed here too and not saved yet: ours goes next
        changed = true;
        if (ch.remove) next = next.filter((x) => x.conditionId !== cid);
        else if (mine.has(cid))
          next = next.map((x) => (x.conditionId === cid ? ch.obj : x));
        else next = [...next, ch.obj];
      }
      if (changed) {
        mapRef.current = next;
        setMappings(next);
      }
    }
    if (newS !== undefined) {
      const merged = { ...DEFAULT_SETTINGS, ...(newS || {}) };
      if (mjson(setRef.current) === b.s) {
        setRef.current = merged;
        setSettings(merged);
      }
      b.s = mjson(merged);
    }
  };

  const doPut = async (payload, { estimateOnly = false } = {}) => {
    const useOps = !!payload.ops && !!estBase.current;
    const body = {
      ...payload,
      [ownerKey]: ownerId,
      // who's saving, for the takeoff's change log (App.jsx keeps the signed-in name)
      userName:
        payload.userName ||
        localStorage.getItem("userDisplayName") ||
        undefined,
    };
    let sentM = null;
    let sentS;
    if (useOps) {
      const eo = estimateOps();
      const ops = { ...payload.ops };
      if (eo.mappings) ops.mappings = sentM = eo.mappings;
      if (eo.settings !== undefined) {
        body.estimateSettings = eo.settings;
        sentS = mjson(eo.settings);
      }
      body.ops = ops;
      delete body.mappings;
      if (estimateOnly && !sentM && sentS === undefined)
        return { _id: payload._id, rev: revRef.current, unchanged: true };
    } else {
      // a new takeoff / an older takeoff without revisions: everything
      body.mappings = mapRef.current;
      body.estimateSettings = setRef.current;
    }
    const data = (
      await axios.put(`${API}/takeoffs3`, body, { headers: authHeaders() })
    ).data;
    if (data?._id) setCurTakeoffId(String(data._id));
    if (typeof data?.rev === "number") {
      ownRevs.current.add(data.rev);
      revRef.current = Math.max(revRef.current, data.rev);
    }
    if (useOps) {
      const b = estBase.current;
      if (sentM) {
        for (const x of sentM.upsert) b.m.set(x.conditionId, mjson(x));
        for (const cid of sentM.remove) b.m.delete(cid);
      }
      if (sentS !== undefined) b.s = sentS;
    } else if (data?._id && typeof data.rev === "number")
      estBase.current = baseFrom(body.mappings, body.estimateSettings);
    applyRemoteEstimate(data?.remote || []);
    return data;
  };
  // one save at a time (the drawing's and the estimate's), so each one diffs against what the server has
  const put = (payload, opts) => {
    const run = queue.current.catch(() => {}).then(() => doPut(payload, opts));
    queue.current = run;
    return run;
  };

  const handleSave = (payload) => put(payload);

  const handleUploadDrawing = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const r = await axios.post(
      `${API}/takeoffs3/upload-drawing?${ownerKey}=${ownerId}`,
      form,
      { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } },
    );
    return r.data;
  };

  const handleRenameDrawing = async (id, name) => {
    const r = await axios.patch(
      `${API}/takeoffs3/${id}/rename`,
      { fileName: name },
      { headers: authHeaders() },
    );
    return r.data.fileName;
  };

  const handlePushToBudget = async (id) =>
    (
      await axios.post(
        `${API}/takeoffs3/${id}/push-to-budget`,
        {},
        { headers: authHeaders() },
      )
    ).data;

  const getCostCodes = useCallback(async () => {
    const r = await axios.get(`${API}/company-profile/cost-codes`, {
      headers: authHeaders(),
    });
    return Array.isArray(r.data) ? r.data : r.data?.costCodes || [];
  }, []);

  // standard assemblies (routes/assemblyTemplateRoutes.js)
  const templateApi = useMemo(
    () => ({
      list: async () =>
        (
          await axios.get(`${API}/assembly-templates`, {
            headers: authHeaders(),
          })
        ).data?.templates || [],
      save: async (t) =>
        (t._id
          ? await axios.put(`${API}/assembly-templates/${t._id}`, t, {
              headers: authHeaders(),
            })
          : await axios.post(`${API}/assembly-templates`, t, {
              headers: authHeaders(),
            })
        ).data?.template,
      remove: async (id) =>
        axios.delete(`${API}/assembly-templates/${id}`, {
          headers: authHeaders(),
        }),
      duplicate: async (id) =>
        (
          await axios.post(
            `${API}/assembly-templates/${id}/duplicate`,
            {},
            { headers: authHeaders() },
          )
        ).data?.template,
      used: (id, count) =>
        axios
          .post(
            `${API}/assembly-templates/${id}/used`,
            { count },
            { headers: authHeaders() },
          )
          .catch(() => {}),
    }),
    [],
  );

  const getCatalog = useCallback(async () => {
    const r = await axios.get(`${API}/item-database/catalog`, {
      headers: authHeaders(),
      params: { isActive: true },
    });
    return r.data?.items || r.data || [];
  }, []);

  const listForViewer = useCallback(
    async () =>
      (
        await axios.get(
          `${API}/takeoffs3/${kind === "opportunity" ? "opportunity" : "project"}/${ownerId}`,
          { headers: authHeaders() },
        )
      ).data,
    [kind, ownerId],
  );

  // estimate page actions
  const saveDraft = async () => {
    if (!estimatePayload) return;
    // metrics + the sheet renderer are display-only — don't send them back
    const {
      condMetrics: _cm,
      condMetricsByArea: _cb,
      renderSheetImage: _rs,
      printDrawings: _pd,
      ...body
    } = estimatePayload;
    // An existing takeoff: save only the estimate's own fields (mappings + settings). The drawing's lines may have
    // been changed by someone since this page opened — the copy here must never overwrite them. (Assembly renames
    // made here reach the drawing screen, which saves them.)
    if (body._id && body.supportsOps && estBase.current) {
      return put(
        {
          _id: body._id,
          baseRev: Math.max(revRef.current, body.rev || 0),
          clientId: estClientId.current,
          ops: { set: {} },
        },
        { estimateOnly: true },
      );
    }
    const doc = await put({ ...body, status: undefined });
    if (doc?._id && !estimatePayload._id)
      setEstimatePayload((p) => ({ ...p, _id: doc._id }));
    return doc;
  };
  const saveAndPush = async () => {
    const doc = await saveDraft();
    const id = doc?._id || estimatePayload?._id;
    if (!id) throw new Error("Open a drawing and save before publishing.");
    const r = await handlePushToBudget(id);
    setEstimatePayload((p) => ({ ...p, status: "published" }));
    return r;
  };

  // rename an assembly from the estimate page: update what the estimate shows AND tell the viewer
  const renameCondition = (condId, name) => {
    setCondRenames((p) => ({ ...p, [condId]: name }));
    setEstimatePayload((p) =>
      p
        ? {
            ...p,
            conditions: (p.conditions || []).map((c) =>
              c.id === condId ? { ...c, name } : c,
            ),
          }
        : p,
    );
  };

  const backToList = () => {
    setActive(null);
    setView("viewer");
    if (ownerId) loadTakeoffs(ownerId);
    loadCounts();
  };

  // ═════════════════════ viewer + estimate ═════════════════════════════════
  if (owner && active) {
    return (
      <div className="flex h-[calc(100vh-110px)] mt-2 mx-4 min-h-[480px] flex-col rounded-2xl bg-gray-950 p-3 ring-1 ring-white/10">
        <div className="mb-2 flex items-center gap-3">
          <button
            onClick={backToList}
            className="rounded-lg bg-gray-700 px-3 py-1.5 text-xs font-medium text-gray-200 hover:bg-gray-600"
          >
            ← All takeoffs
          </button>
          <span className="truncate text-sm font-semibold text-gray-100">
            {ownerLabel}
          </span>
          <span
            className={cls(
              "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
              kind === "opportunity"
                ? "bg-sky-500/15 text-sky-300"
                : "bg-orange-500/15 text-orange-300",
            )}
          >
            {kind === "opportunity" ? "Bid" : "Project"}
          </span>
          {toast && (
            <span className="ml-2 flex items-center gap-2 rounded-lg bg-emerald-500/10 px-2.5 py-1 text-xs text-emerald-300">
              {toast}
              {view === "viewer" && (
                <button
                  onClick={() => setView("rfis")}
                  className="font-semibold underline"
                >
                  Back to RFIs
                </button>
              )}
            </span>
          )}
          {snipMode && view === "viewer" && (
            <span className="ml-2 rounded-lg bg-orange-500/15 px-2.5 py-1 text-xs text-orange-200">
              ✂ Drag a box round the area to cut · zoom / scroll first if needed
              · Esc to cancel
            </span>
          )}
          {pinMode && view === "viewer" && (
            <span className="ml-2 rounded-lg bg-orange-500/15 px-2.5 py-1 text-xs text-orange-200">
              Click the drawing where the RFI applies · Esc to cancel
            </span>
          )}
          <div className="ml-auto inline-flex rounded-xl bg-gray-800 p-1 text-xs">
            {[
              ["viewer", "Drawing"],
              ...(view === "estimate" ? [["estimate", "Estimate"]] : []),
              ["specs", "Specs"],
              [
                "rfis",
                `RFIs${rfis.filter((r) => ["draft", "sent"].includes(r.status)).length ? ` (${rfis.filter((r) => ["draft", "sent"].includes(r.status)).length})` : ""}`,
              ],
            ].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setView(k)}
                className={cls(
                  "rounded-lg px-3 py-1 font-medium",
                  view === k
                    ? "bg-orange-500 text-white"
                    : "text-gray-300 hover:text-white",
                )}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        {error && (
          <div className="mb-2 rounded border border-red-800 bg-red-900/30 px-3 py-2 text-xs text-red-300">
            {error}
          </div>
        )}
        {snipShot && (
          <ImageMarkup
            src={snipShot.src}
            title="Picture for the RFI"
            sub={`Cut from drawing page ${snipShot.page} — circle or point at what you're asking about`}
            saving={snipSaving}
            onCancel={() => setSnipShot(null)}
            onSave={saveSnip}
          >
            {!(snipShot.target && typeof snipShot.target === "object") && (
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={snipForm.rfiId}
                  onChange={(e) =>
                    setSnipForm((f) => ({
                      ...f,
                      rfiId: e.target.value,
                      itemId: "new",
                    }))
                  }
                  className="rounded-lg border border-gray-700 bg-gray-950 px-2 py-1.5 text-xs text-gray-200"
                >
                  <option value="new">A new RFI</option>
                  {rfis
                    .filter((r) => ["draft", "sent"].includes(r.status))
                    .map((r) => (
                      <option key={r._id} value={r._id}>
                        RFI {r.number} — {r.title}
                      </option>
                    ))}
                </select>
                {snipForm.rfiId !== "new" && (
                  <select
                    value={snipForm.itemId}
                    onChange={(e) =>
                      setSnipForm((f) => ({ ...f, itemId: e.target.value }))
                    }
                    className="max-w-[14rem] rounded-lg border border-gray-700 bg-gray-950 px-2 py-1.5 text-xs text-gray-200"
                  >
                    <option value="new">New question…</option>
                    {(
                      rfis.find((r) => r._id === snipForm.rfiId)?.items || []
                    ).map((it, k) => (
                      <option key={it.id} value={it.id}>
                        Q{k + 1}:{" "}
                        {String(it.question || "(blank)").slice(0, 50)}
                      </option>
                    ))}
                  </select>
                )}
                {(snipForm.rfiId === "new" || snipForm.itemId === "new") && (
                  <input
                    value={snipForm.question}
                    onChange={(e) =>
                      setSnipForm((f) => ({ ...f, question: e.target.value }))
                    }
                    placeholder="The question"
                    className="w-64 rounded-lg border border-gray-700 bg-gray-950 px-2.5 py-1.5 text-xs text-gray-100 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
                  />
                )}
              </div>
            )}
          </ImageMarkup>
        )}
        {pinAsk && (
          <div
            className="fixed inset-0 z-[2000] flex items-start justify-center bg-black/60 p-4 pt-[14vh]"
            onMouseDown={() => setPinAsk(null)}
          >
            <div
              className="w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-gray-900 shadow-2xl"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="border-b border-white/10 px-4 py-3">
                <div className="text-sm font-semibold text-white">
                  RFI on the drawing
                </div>
                <div className="text-[11px] text-gray-500">
                  Page {pinAsk.page} — the pin stays on the drawing and links to
                  the question
                </div>
              </div>
              <div className="space-y-3 p-4">
                <label className="block">
                  <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                    Question
                  </span>
                  <textarea
                    autoFocus
                    rows={3}
                    value={pinForm.question}
                    onChange={(e) =>
                      setPinForm((f) => ({ ...f, question: e.target.value }))
                    }
                    placeholder="e.g. Wall type at this corridor isn't tagged — please confirm the partition type and height."
                    className="w-full rounded-lg border border-gray-700 bg-gray-950 px-2.5 py-2 text-[13px] text-gray-100 placeholder-gray-600 focus:border-orange-500 focus:outline-none"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                    Add to
                  </span>
                  <select
                    value={pinForm.rfiId}
                    onChange={(e) =>
                      setPinForm((f) => ({ ...f, rfiId: e.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-700 bg-gray-950 px-2.5 py-2 text-[13px] text-gray-100"
                  >
                    <option value="new">A new RFI</option>
                    {rfis
                      .filter((r) => r.status === "draft")
                      .map((r) => (
                        <option key={r._id} value={r._id}>
                          RFI {r.number} — {r.title} ({r.items?.length || 0}{" "}
                          questions)
                        </option>
                      ))}
                  </select>
                </label>
              </div>
              <div className="flex justify-end gap-2 border-t border-white/10 px-4 py-3">
                <button
                  onClick={() => setPinAsk(null)}
                  className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
                >
                  Cancel
                </button>
                <button
                  onClick={savePinAsk}
                  disabled={!pinForm.question.trim()}
                  className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-400 disabled:opacity-40"
                >
                  Pin it
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="relative min-h-0 flex-1">
          {/* viewer stays mounted so no drawing state is lost while pricing */}
          <div className={cls("h-full", view !== "viewer" && "hidden")}>
            <TakeoffViewer
              key={active.key}
              {...{ [ownerKey]: ownerId }}
              fileUrl={active.fileUrl}
              initialData={active.initialData}
              onSave={handleSave}
              onPushToBudget={
                kind === "project" ? handlePushToBudget : undefined
              }
              onUploadDrawing={handleUploadDrawing}
              onRenameDrawing={handleRenameDrawing}
              conditionRenames={condRenames}
              onDuplicateCondition={(fromId, toId) =>
                // the copy gets the original's estimate items (new item ids), so it prices the same until edited
                setMappings((prev) => {
                  const rid = () => Math.random().toString(36).slice(2, 10);
                  const copies = (prev || [])
                    .filter((m) => m.conditionId === fromId)
                    .map((m) => ({
                      ...JSON.parse(JSON.stringify(m)),
                      conditionId: toId,
                      items: (m.items || []).map((it) => ({
                        ...it,
                        id: rid(),
                      })),
                    }));
                  return copies.length ? [...prev, ...copies] : prev;
                })
              }
              getCatalog={getCatalog}
              listTakeoffs={listForViewer}
              onOpenTakeoff={(id) =>
                openById(id).catch(() =>
                  setError("Could not open that takeoff."),
                )
              }
              onRemoteChanges={applyRemoteEstimate}
              onImmersiveChange={setViewerFull}
              rfiPins={rfiPins}
              pinMode={!!pinMode}
              onPinModeChange={(on) => {
                setSnipMode(null);
                setPinMode(on ? "pick" : null);
              }}
              onRfiPinPlace={placeRfiPin}
              snipMode={!!snipMode}
              onSnipModeChange={(on) =>
                on ? startSnip("pick") : setSnipMode(null)
              }
              onSnip={gotSnip}
              onRfiPinClick={(p) => {
                setFocusRfiId({ id: p.rfiId, t: Date.now() });
                setView("rfis");
              }}
              focusPin={focusPin}
              getPayloadRef={viewerDataRef}
              onGoToEstimate={(payload) => {
                setEstimatePayload(payload);
                setView("estimate");
              }}
            />
          </div>
          {view !== "viewer" && (
            <MovableHost
              fullscreen={viewerFull || estFull}
              className="absolute inset-0"
              overlayClassName="fixed inset-0 z-[1350] flex flex-col bg-gray-950"
            >
              {view === "specs" && (
                <div className="absolute inset-0 overflow-hidden rounded-xl border border-white/10">
                  <SpecScanner
                    {...{ [ownerKey]: ownerId }}
                    ownerLabel={ownerLabel}
                    onAddToRfi={addToRfiInbox}
                    onBack={
                      viewerFull || estFull
                        ? () => setView("viewer")
                        : undefined
                    }
                  />
                </div>
              )}
              {view === "rfis" && (
                <div className="absolute inset-0 overflow-hidden rounded-xl border border-white/10">
                  <RfiManager
                    {...{ [ownerKey]: ownerId }}
                    ownerLabel={ownerLabel}
                    rfis={rfis}
                    setRfis={setRfis}
                    getDrawing={() => viewerDataRef.current?.() || null}
                    takeoffId={curTakeoffId}
                    inbox={rfiInbox}
                    onInboxTaken={(keys) =>
                      setRfiInbox((l) => l.filter((x) => !keys.includes(x.key)))
                    }
                    onShowPin={(p) => {
                      if (
                        p.takeoffId &&
                        curTakeoffId &&
                        String(p.takeoffId) !== String(curTakeoffId)
                      ) {
                        setToast(
                          "That pin is on another takeoff of this project",
                        );
                        return;
                      }
                      setView("viewer");
                      setFocusPin({
                        page: p.page,
                        lat: p.lat,
                        lng: p.lng,
                        t: Date.now(),
                      });
                    }}
                    onPlacePin={({ rfiId, itemId }) => {
                      setSnipMode(null);
                      setPinMode({ rfiId, itemId });
                      setView("viewer");
                    }}
                    onSnip={startSnip}
                    focusRfiId={focusRfiId}
                    reportMeta={{ ...rfiMeta, project: ownerLabel }}
                    onBack={
                      viewerFull || estFull
                        ? () => setView("viewer")
                        : undefined
                    }
                  />
                </div>
              )}
              {view === "estimate" && estimatePayload && (
                <div className="absolute inset-0 overflow-hidden rounded-xl border border-white/10">
                  <TakeoffEstimatePage
                    conditions={estimatePayload.conditions || []}
                    condMetrics={estimatePayload.condMetrics || {}}
                    condMetricsByArea={
                      estimatePayload.condMetricsByArea || null
                    }
                    printDrawings={estimatePayload.printDrawings || null}
                    assemblies={estimatePayload.assemblies || []}
                    shapes={estimatePayload.shapes || []}
                    scales={estimatePayload.scales || {}}
                    pageNames={estimatePayload.pageNames || {}}
                    excludedPages={estimatePayload.excludedPages || []}
                    pageBidAreas={estimatePayload.pageBidAreas || {}}
                    units={estimatePayload.units || []}
                    renderSheetImage={estimatePayload.renderSheetImage || null}
                    mappings={mappings}
                    setMappings={setMappings}
                    settings={settings}
                    setSettings={setSettings}
                    getCatalog={getCatalog}
                    status={estimatePayload.status || "draft"}
                    ownerKind={kind}
                    ownerLabel={`${ownerLabel} — ${estimatePayload.fileName || ""}`}
                    onRenameCondition={renameCondition}
                    onSaveDraft={saveDraft}
                    autoSave={
                      !!(
                        estimatePayload._id &&
                        estimatePayload.supportsOps &&
                        estBase.current
                      )
                    }
                    onAutoSave={saveDraft}
                    templateApi={templateApi}
                    getCostCodes={getCostCodes}
                    reportMeta={{
                      project: ownerLabel,
                      drawing: estimatePayload.fileName || "",
                      client:
                        kind === "opportunity"
                          ? owner?.account?.companyName || ""
                          : owner?.clientName ||
                            owner?.client?.companyName ||
                            "",
                      preparedBy: localStorage.getItem("userDisplayName") || "",
                      company: localStorage.getItem("companyName") || "",
                      estimateNo: estimatePayload._id
                        ? `EST-${String(estimatePayload._id).slice(-6).toUpperCase()}`
                        : "",
                    }}
                    onSaveAndPush={kind === "project" ? saveAndPush : undefined}
                    onBack={() => setView("viewer")}
                    fullscreen={viewerFull || estFull}
                    onToggleFullscreen={
                      viewerFull ? undefined : () => setEstFull((v) => !v)
                    }
                    takeoffId={estimatePayload._id || null}
                    ownerId={ownerId}
                    apiBase={API}
                  />
                </div>
              )}
            </MovableHost>
          )}
        </div>
      </div>
    );
  }

  // ═════════════════════ specs / RFIs without a drawing open ═════════════════
  if (owner && panel) {
    return (
      <div className="mt-2 mx-4 flex h-[calc(100vh-110px)] min-h-[480px] flex-col overflow-hidden rounded-2xl bg-gray-950 ring-1 ring-white/10">
        {panel === "specs" ? (
          <SpecScanner
            {...{ [ownerKey]: ownerId }}
            ownerLabel={ownerLabel}
            onAddToRfi={addToRfiInbox}
            onBack={() => setPanel(null)}
          />
        ) : (
          <RfiManager
            {...{ [ownerKey]: ownerId }}
            ownerLabel={ownerLabel}
            rfis={rfis}
            setRfis={setRfis}
            inbox={rfiInbox}
            onInboxTaken={(keys) =>
              setRfiInbox((l) => l.filter((x) => !keys.includes(x.key)))
            }
            focusRfiId={focusRfiId}
            reportMeta={{ ...rfiMeta, project: ownerLabel }}
            onBack={() => setPanel(null)}
          />
        )}
      </div>
    );
  }

  // ═════════════════════ launcher ══════════════════════════════════════════
  return (
    <div className="py-4 mx-4">
      <div className="mx-auto min-h-[calc(100vh-150px)] rounded-2xl bg-gray-950 p-6 shadow-xl ring-1 ring-white/10">
        <div className="mb-5 flex flex-wrap items-end gap-4">
          <div>
            <h1 className="text-xl font-semibold text-white">Takeoff</h1>
            <p className="text-sm text-gray-400">
              Measure drawings and build the estimate — on a live project or
              while you're still bidding.
            </p>
          </div>
          <div className="ml-auto inline-flex rounded-xl bg-gray-800 p-1">
            {[
              ["project", "Projects"],
              ["opportunity", "Opportunities"],
            ].map(([k, l]) => (
              <button
                key={k}
                onClick={() => switchKind(k)}
                className={cls(
                  "rounded-lg px-4 py-1.5 text-sm font-medium transition",
                  kind === k
                    ? "bg-orange-500 text-white shadow"
                    : "text-gray-400 hover:text-gray-200",
                )}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mb-3 rounded border border-red-800 bg-red-900/30 px-3 py-2 text-xs text-red-300">
            {error}
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
          {/* owner list */}
          <aside className="flex max-h-[calc(100vh-220px)] flex-col rounded-2xl border border-white/10 bg-gray-900">
            <div className="space-y-2 border-b border-white/10 p-3">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={
                  kind === "project"
                    ? "Search projects…"
                    : "Search opportunities…"
                }
                className="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
              />
              {kind === "opportunity" && (
                <div className="flex gap-1">
                  {[
                    ["open", "Open bids"],
                    ["all", "All"],
                  ].map(([k, l]) => (
                    <button
                      key={k}
                      onClick={() => setOppStatus(k)}
                      className={cls(
                        "flex-1 rounded-md py-1 text-xs font-medium",
                        oppStatus === k
                          ? "bg-white/10 text-white"
                          : "text-gray-500 hover:text-gray-300",
                      )}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2">
              {listLoading && (
                <p className="py-8 text-center text-xs text-gray-500">
                  Loading…
                </p>
              )}
              {!listLoading && owners.length === 0 && (
                <p className="py-8 text-center text-xs text-gray-500">
                  Nothing found.
                </p>
              )}
              {owners.map((o) => {
                const sel = ownerId === o._id;
                const c = counts[kind][o._id] || 0;
                return (
                  <button
                    key={o._id}
                    onClick={() => pickOwner(o)}
                    className={cls(
                      "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition",
                      sel
                        ? "bg-orange-500/15 ring-1 ring-orange-500/50"
                        : "hover:bg-white/5",
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div
                        className={cls(
                          "truncate text-sm font-medium",
                          sel ? "text-orange-200" : "text-gray-100",
                        )}
                      >
                        {kind === "project" ? o.projectName || o.name : o.title}
                      </div>
                      <div className="flex items-center gap-2 truncate text-[11px] text-gray-500">
                        {kind === "project" ? (
                          o.projectNumber && <span>#{o.projectNumber}</span>
                        ) : (
                          <>
                            <span
                              className={cls(
                                "rounded-full px-1.5 py-px text-[9px] font-semibold",
                                STATUS_STYLE[o.status] ||
                                  "bg-gray-500/20 text-gray-400",
                              )}
                            >
                              {o.status}
                            </span>
                            <span className="truncate">
                              {o.account?.companyName}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    {c > 0 && (
                      <span className="shrink-0 rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-gray-200">
                        {c}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </aside>

          {/* takeoffs */}
          <section className="min-h-[300px] rounded-2xl border border-white/10 bg-gray-900 p-4">
            {!owner ? (
              <div className="grid h-full place-items-center py-20 text-center">
                <div>
                  <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-orange-500/10 text-xl text-orange-300">
                    ◰
                  </div>
                  <p className="text-sm font-medium text-gray-200">
                    Choose a {kind === "project" ? "project" : "opportunity"}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    {kind === "project"
                      ? "Takeoffs here can be published straight to the project budget."
                      : "Price the job during bidding — the takeoff can follow the job once it's awarded."}
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-4 flex flex-wrap items-center gap-3">
                  <div className="min-w-0">
                    <h2 className="truncate text-base font-semibold text-white">
                      {ownerLabel}
                    </h2>
                    <p className="text-xs text-gray-500">
                      {takeoffs.length} takeoff
                      {takeoffs.length === 1 ? "" : "s"}
                    </p>
                  </div>
                  <button
                    onClick={() => setPanel("specs")}
                    className="ml-auto rounded-lg bg-white/10 px-3 py-2 text-sm font-semibold text-white hover:bg-white/15"
                  >
                    Spec scanner
                  </button>
                  <button
                    onClick={() => setPanel("rfis")}
                    className="rounded-lg bg-white/10 px-3 py-2 text-sm font-semibold text-white hover:bg-white/15"
                  >
                    RFIs
                    {rfis.filter((r) => ["draft", "sent"].includes(r.status))
                      .length
                      ? ` (${rfis.filter((r) => ["draft", "sent"].includes(r.status)).length})`
                      : ""}
                  </button>
                  <button
                    onClick={newTakeoff}
                    className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-400"
                  >
                    + New takeoff
                  </button>
                </div>
                {takeoffsLoading ? (
                  <p className="py-16 text-center text-xs text-gray-500">
                    Loading…
                  </p>
                ) : takeoffs.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-white/10 py-16 text-center">
                    <p className="text-sm text-gray-300">No takeoffs yet</p>
                    <p className="mt-1 text-xs text-gray-500">
                      Start one and open a drawing PDF.
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {takeoffs.map((t) => (
                      <TakeoffCard
                        key={t._id}
                        t={t}
                        opening={openingId === t._id}
                        onOpen={openTakeoff}
                        onRename={renameTakeoff}
                        onDelete={deleteTakeoff}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
